import { ApiServiceBase } from './base/api-service-base';
import {
	BreakdownRequest,
	BreakdownResult,
	ChurnInsightsRequest,
	ChurnInsightsResult,
	CustomerTimeline,
	JourneyCustomersPage,
	JourneyCustomersRequest,
	JourneyExploreResult,
	JourneyExportRequest,
	JourneyFilterState,
	JourneyFlowRequest,
	JourneyFlowResult,
	JourneyMeta,
	SavedView,
	SavedViewState,
	TimeBetweenRequest,
	TimeBetweenResult,
} from '../../models/journey-explorer/journey-explorer';

export interface IJourneyExplorerApiService {
	getMeta(): Promise<JourneyMeta>;
	refresh(): Promise<void>;
	explore(filters: JourneyFilterState, signal?: AbortSignal): Promise<JourneyExploreResult>;
	getCustomers(request: JourneyCustomersRequest, signal?: AbortSignal): Promise<JourneyCustomersPage>;
	getFlow(request: JourneyFlowRequest, signal?: AbortSignal): Promise<JourneyFlowResult>;
	getChurnInsights(request: ChurnInsightsRequest, signal?: AbortSignal): Promise<ChurnInsightsResult>;
	getBreakdown(request: BreakdownRequest, signal?: AbortSignal): Promise<BreakdownResult>;
	getTimeBetween(request: TimeBetweenRequest, signal?: AbortSignal): Promise<TimeBetweenResult>;
	getTimeline(customerId: string, signal?: AbortSignal): Promise<CustomerTimeline>;
	exportCustomers(request: JourneyExportRequest): Promise<Blob>;
	getSavedViews(): Promise<SavedView[]>;
	saveView(name: string, state: SavedViewState): Promise<SavedView>;
	deleteView(id: string): Promise<void>;
}

const baseEndpoint = 'journey-explorer';

export class JourneyExplorerApiService extends ApiServiceBase implements IJourneyExplorerApiService {
	public async getMeta(): Promise<JourneyMeta> {
		return await this.GetFromApi<JourneyMeta>(`${baseEndpoint}/meta`);
	}

	public async refresh(): Promise<void> {
		await this.PostToApi<void>(`${baseEndpoint}/refresh`);
	}

	public async explore(filters: JourneyFilterState, signal?: AbortSignal): Promise<JourneyExploreResult> {
		return await this.PostToApi<JourneyExploreResult>(
			`${baseEndpoint}/explore`,
			filters,
			'json',
			undefined,
			signal
		);
	}

	public async getCustomers(request: JourneyCustomersRequest, signal?: AbortSignal): Promise<JourneyCustomersPage> {
		return await this.PostToApi<JourneyCustomersPage>(
			`${baseEndpoint}/customers`,
			request,
			'json',
			undefined,
			signal
		);
	}

	public async getFlow(request: JourneyFlowRequest, signal?: AbortSignal): Promise<JourneyFlowResult> {
		return await this.PostToApi<JourneyFlowResult>(`${baseEndpoint}/flow`, request, 'json', undefined, signal);
	}

	public async getChurnInsights(request: ChurnInsightsRequest, signal?: AbortSignal): Promise<ChurnInsightsResult> {
		return await this.PostToApi<ChurnInsightsResult>(
			`${baseEndpoint}/churn-insights`,
			request,
			'json',
			undefined,
			signal
		);
	}

	public async getBreakdown(request: BreakdownRequest, signal?: AbortSignal): Promise<BreakdownResult> {
		return await this.PostToApi<BreakdownResult>(`${baseEndpoint}/breakdown`, request, 'json', undefined, signal);
	}

	public async getTimeBetween(request: TimeBetweenRequest, signal?: AbortSignal): Promise<TimeBetweenResult> {
		return await this.PostToApi<TimeBetweenResult>(
			`${baseEndpoint}/time-between`,
			request,
			'json',
			undefined,
			signal
		);
	}

	public async getTimeline(customerId: string, signal?: AbortSignal): Promise<CustomerTimeline> {
		return await this.PostToApi<CustomerTimeline>(
			`${baseEndpoint}/timeline`,
			{ customer_id: customerId },
			'json',
			undefined,
			signal
		);
	}

	public async exportCustomers(request: JourneyExportRequest): Promise<Blob> {
		return await this.PostToApi<Blob>(`${baseEndpoint}/customers/export`, request, 'blob');
	}

	public async getSavedViews(): Promise<SavedView[]> {
		return await this.GetFromApi<SavedView[]>(`${baseEndpoint}/saved-views`);
	}

	public async saveView(name: string, state: SavedViewState): Promise<SavedView> {
		return await this.PostToApi<SavedView>(`${baseEndpoint}/saved-views`, { name, state });
	}

	public async deleteView(id: string): Promise<void> {
		await this.DeleteFromApi<void>(`${baseEndpoint}/saved-views/${encodeURIComponent(id)}`);
	}
}
